import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader.js";
import { TGALoader } from "three/examples/jsm/loaders/TGALoader.js";
import * as SkeletonUtils from "three/examples/jsm/utils/SkeletonUtils.js";
import { ResourceID } from "./resource_id.js";
import { error } from "../utils/logger.js";
import { Vfx3DGlobalAtlas } from "../vfx_3d/core/textures/vfx_3d_global_atlas.js";
import { VFX_3D_ATLAS_CONFIG, VFX_3D_ATLAS_SIZE } from "../vfx_3d/vfx_3d_atlas_config.js";
import { GlobalSettings } from "../global_settings.js";
import { buildAndRegisterGlassShards } from "../vfx_3d/effects/jam/glass_shard_texture_builder.js";

class EmptyFbxTextureLoader extends THREE.Loader<THREE.DataTexture>
{
  public load(
    _url: string,
    onLoad?: ( texture: THREE.DataTexture ) => void
  ): THREE.DataTexture
  {
    const texture = new THREE.DataTexture(
      new Uint8Array( [255, 255, 255, 0] ),
      1,
      1,
      THREE.RGBAFormat
    );
    texture.needsUpdate = true;
    onLoad?.( texture );
    return texture;
  }
}

const fbx_loading_manager = new THREE.LoadingManager();
// Entity classes replace every FBX material with a game material. Skip stale
// texture references embedded in the source FBX files without HTTP requests.
fbx_loading_manager.addHandler( /\.png$/i, new EmptyFbxTextureLoader( fbx_loading_manager ) );

export class ResourceManager 
{
  private static models: Map<ResourceID, THREE.Object3D> = new Map();
  private static textures: Map<ResourceID, THREE.Texture> = new Map();
  private static animations: Map<ResourceID, THREE.AnimationClip[]> = new Map();

  private static gltf_loader = new GLTFLoader();
  private static fbx_loader = new FBXLoader( fbx_loading_manager );
  private static tex_loader = new THREE.TextureLoader();
  private static tga_loader = new TGALoader();
  private static file_loader = new THREE.FileLoader();

  private static resource_bytes = new Map<string, number>();
  private static downloaded_bytes = 0;

  private static total_to_load = 0;
  private static loaded = 0;

  static onProgress: ( ( progress: number ) => void ) | null = null;

  public static is_loading_finished: boolean = true;

  private static model_queue: { name: ResourceID; path: string; flip_v: boolean; fbx_axes: boolean }[] = [];
  private static texture_queue: { name: ResourceID; path: string; srgb: boolean; flip_y: boolean }[] = [];

  /**
   * `flip_v` mirrors the UVs of a GLB (v -> 1 - v) so a model exported for the
   * FBX texture convention (its maps load with flipY on) keeps working as-is:
   * same textures, normal-map scale and UV maths as the FBX it replaced.
   * `fbx_axes` stores a Y-up GLB mesh the way Blender's FBX export did: geometry
   * lying along local Z, the mesh node turned -90° about X. The world shape is
   * unchanged; code that works in the mesh's local axes (plastic long/2x2 UVs)
   * sees the frame it was written for.
   */
  public static addModel( name: ResourceID, path: string, options?: { flip_v?: boolean; fbx_axes?: boolean } )
  {
    this.model_queue.push( { name, path, flip_v: options?.flip_v ?? false, fbx_axes: options?.fbx_axes ?? false } );
  }

  public static addTexture(
    name: ResourceID,
    path: string,
    srgb: boolean = true,
    flip_y: boolean = false
  )
  {
    this.texture_queue.push( { name, path, srgb, flip_y } );
  }

  public static addNormalMap( name: ResourceID, path: string )
  {
    if ( !GlobalSettings.USE_NORMAL_MAPS )
      return;

    this.addTexture( name, path, false, true );
  }

  private static load_promise: Promise<void> | null = null;
  private static boosted = false;

  public static startLoading(): Promise<void>
  {
    if ( !this.load_promise )
      this.load_promise = this.loadAll( 8 );

    return this.load_promise;
  }

  public static whenReady(): Promise<void>
  {
    this.boosted = true;
    return this.startLoading();
  }

  public static pacingDelay(): Promise<void>
  {
    if ( this.boosted )
      return new Promise( resolve => setTimeout( resolve, 0 ) );

    return new Promise( resolve =>
    {
      const ric = ( globalThis as any ).requestIdleCallback;
      if ( ric )
        ric( () => resolve(), { timeout: 500 } );
      else
        setTimeout( resolve, 48 );
    } );
  }

  public static async loadAll( batch_size: number = 0 ): Promise<void>
  {
    this.file_loader.setResponseType( "blob" );

    this.total_to_load = this.model_queue.length + this.texture_queue.length;
    this.loaded = 0;
    this.downloaded_bytes = 0;
    this.resource_bytes.clear();

    const tasks: ( () => Promise<any> )[] = [];

    for ( const m of this.model_queue )
      tasks.push( () => this.loadModel( m.name, m.path, m.flip_v, m.fbx_axes ) );

    for ( const t of this.texture_queue )
      tasks.push( () => this.loadTexture( t.name, t.path, t.srgb, t.flip_y ) );

    if ( batch_size <= 0 )
    {
      await Promise.all( tasks.map( task => task() ) );
    }
    else
    {
      let i = 0;
      while ( i < tasks.length )
      {
        const width = this.boosted ? batch_size : 1;
        await Promise.all( tasks.slice( i, i + width ).map( task => task() ) );
        i += width;
        await this.pacingDelay();
      }
    }

    buildAndRegisterGlassShards();
    Vfx3DGlobalAtlas.init( VFX_3D_ATLAS_CONFIG, VFX_3D_ATLAS_SIZE );

    this.is_loading_finished = true;
  }

  private static async loadModel( name: ResourceID, path: string, flip_v = false, fbx_axes = false )
  {
    const clean_path = path.split( "?" )[0].toLowerCase();

    if ( clean_path.endsWith( ".fbx" ) )
    {
      return new Promise<void>( resolve =>
      {
        this.fbx_loader.load(
          path,
          model =>
          {
            this.models.set( name, model );
            this.animations.set( name, model.animations ?? [] );
            this.updateProgress();
            resolve();
          },
          xhr =>
          {
            const prev = this.resource_bytes.get( path ) ?? 0;
            const delta = xhr.loaded - prev;

            if ( delta > 0 )
            {
              this.downloaded_bytes += delta;
              this.resource_bytes.set( path, xhr.loaded );
            }
          },
          err =>
          {
            error( "Failed to load model", path, err );
            this.updateProgress();
            resolve();
          }
        );
      } );
    }

    return new Promise<void>( resolve =>
    {
      this.gltf_loader.load(
        path,
        gltf =>
        {
          if ( flip_v )
            ResourceManager.flipUvV( gltf.scene );
          if ( fbx_axes )
            ResourceManager.toFbxAxes( gltf.scene );
          this.models.set( name, gltf.scene );
          this.animations.set( name, gltf.animations ?? [] );

          this.updateProgress();
          resolve();
        },
        xhr =>
        {
          const prev = this.resource_bytes.get( path ) ?? 0;
          const delta = xhr.loaded - prev;

          if ( delta > 0 )
          {
            this.downloaded_bytes += delta;
            this.resource_bytes.set( path, xhr.loaded );
          }
        },
        err =>
        {
          error( "Failed to load model", path, err );
          this.updateProgress();
          resolve();
        }
      );
    } );
  }

  private static flipUvV( root: THREE.Object3D ): void
  {
    root.traverse( child =>
    {
      const geometry = ( child as THREE.Mesh ).isMesh ? ( child as THREE.Mesh ).geometry as THREE.BufferGeometry : null;
      const uv = geometry?.getAttribute( "uv" );
      if ( !uv )
        return;

      for ( let i = 0; i < uv.count; i++ )
        uv.setY( i, 1 - uv.getY( i ) );
      uv.needsUpdate = true;
    } );
  }

  private static toFbxAxes( root: THREE.Object3D ): void
  {
    const lay_down = new THREE.Matrix4().makeRotationX( Math.PI * 0.5 );
    root.traverse( child =>
    {
      const mesh = child as THREE.Mesh;
      if ( !mesh.isMesh )
        return;

      mesh.geometry.applyMatrix4( lay_down );
      mesh.rotation.x -= Math.PI * 0.5;
    } );
  }

  private static async loadTexture(
    name: ResourceID,
    path: string,
    srgb: boolean,
    flip_y: boolean
  )
  {
    return new Promise<void>( resolve =>
    {
      this.file_loader.load(
        path,

        blob =>
        {
          const url = URL.createObjectURL( blob as any );

          this.tex_loader.load(
            url,
            tex =>
            {
              URL.revokeObjectURL( url );

              tex.flipY = flip_y;
              tex.colorSpace = srgb
                ? THREE.SRGBColorSpace
                : THREE.NoColorSpace;

              this.textures.set( name, tex );

              this.resource_bytes.delete( path );

              this.updateProgress();
              resolve();
            },
            undefined,
            () =>
            {
              URL.revokeObjectURL( url );

              this.updateProgress();
              resolve();
            }
          );
        },

        xhr =>
        {
          const prev = this.resource_bytes.get( path ) ?? 0;
          const delta = xhr.loaded - prev;

          if ( delta > 0 )
          {
            this.downloaded_bytes += delta;
            this.resource_bytes.set( path, xhr.loaded );
          }
        },

        () =>
        {
          this.updateProgress();
          resolve();
        }
      );
    } );
  }

  public static getDownloadedBytes(): number
  {
    return this.downloaded_bytes;
  }

  public static getDownloadedCount(): number
  {
    return this.loaded;
  }

  public static getTotalCount(): number
  {
    return this.total_to_load;
  }

  private static updateProgress()
  {
    this.loaded++;
    if ( this.onProgress )
      this.onProgress( this.loaded / this.total_to_load );
  }

  public static getModel( name: ResourceID ): THREE.Object3D
  {
    return SkeletonUtils.clone( this.models.get( name )! );
  }

  public static getAnimations( name: ResourceID ): THREE.AnimationClip[]
  {
    return this.animations.get( name )!;
  }

  public static getTexture( name: ResourceID ): THREE.Texture
  {
    return this.textures.get( name )!;
  }

  public static setTexture( name: ResourceID, texture: THREE.Texture ): void
  {
    this.textures.set( name, texture );
  }

  // Returns null while normal maps are off, so a material never ends up with an
  // undefined map because its texture was skipped at load time.
  public static getNormalMap( name: ResourceID ): THREE.Texture | null
  {
    if ( !GlobalSettings.USE_NORMAL_MAPS )
      return null;

    return this.textures.get( name ) ?? null;
  }

  public static dispose()
  {
    this.models.forEach( obj =>
    {
      obj.traverse( c =>
      {
        if ( ( c as any ).geometry ) ( c as any ).geometry.dispose();
        if ( ( c as any ).material )
        {
          const m = ( c as any ).material;
          Array.isArray( m ) ? m.forEach( x => x.dispose() ) : m.dispose();
        }
      } );
    } );

    this.textures.forEach( t => t.dispose() );

    this.models.clear();
    this.textures.clear();
    this.animations.clear();
  }
}
